import { Component, Input, forwardRef, Optional, Self } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NgControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
@Component({
  selector: 'ui-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [],
  template: `
    <div class="flex flex-col gap-1">
      <label
        *ngIf="label"
        [for]="id"
        class="text-sm font-medium text-slate-700"
      >
        {{ label }}
        <span *ngIf="required" class="text-danger-500">*</span>
      </label>

      <div class="relative">
        @if(type === 'textarea') {
        <textarea
          [id]="id"
          [placeholder]="placeholder"
          [value]="value"
          [disabled]="disabled"
          (input)="onInput($event)"
          (blur)="onTouched()"
          [class]="getInputClasses()"
          [name]="name"
        ></textarea>
        } @else if(type === 'checkbox') {
        <button
          type="button"
          [id]="id"
          (click)="onCheckboxChange($event)"
          (blur)="onTouched()"
          [disabled]="disabled"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          [class.bg-primary-600]="value === true"
          [class.bg-gray-200]="value !== true"
          role="switch"
          [attr.aria-checked]="value === true"
        >
          <span class="sr-only">{{ label }}</span>
          <span
            aria-hidden="true"
            class="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            [class.translate-x-5]="value === true"
            [class.translate-x-0]="value !== true"
          ></span>
        </button>
        } @else {
        <input
          [id]="id"
          [type]="type"
          [placeholder]="placeholder"
          [value]="value"
          [disabled]="disabled"
          [required]="required"
          [max]="max"
          (input)="onInput($event)"
          (blur)="onTouched()"
          [class]="getInputClasses()"
          [autocomplete]="autocomplete"
          [readonly]="readonly"
          [name]="name"
        />
        }

        <div *ngIf="shouldShowError()" class="mt-1 text-sm text-danger-600">
          {{ getErrorMessage() }}
        </div>
        <div
          *ngIf="helperText && !shouldShowError()"
          class="mt-1 text-sm text-slate-500"
        >
          {{ helperText }}
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      input:read-only {
        background-color: #f0f0f0;
      }
    `,
  ],
})
export class InputComponent implements ControlValueAccessor {
  @Input() id: string = '';
  @Input() label?: string;
  @Input() type:
    | 'text'
    | 'number'
    | 'email'
    | 'password'
    | 'checkbox'
    | 'date'
    | 'textarea' = 'text';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() error?: string;
  @Input() autocomplete: string = '';
  @Input() readonly: boolean = false;
  @Input() max?: string;
  @Input() helperText?: string;
  @Input() name?: string;
  value: string | boolean = '';
  touched: boolean = false;

  onChange = (value: string | boolean) => {};
  onTouched = () => {};

  constructor(@Optional() @Self() private ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  getInputClasses(): string {
    return `
      w-full
      rounded
      border
      px-3
      py-2
      text-sm
      transition-colors
      disabled:bg-slate-50
      disabled:text-slate-500
      ${
        this.shouldShowError()
          ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
          : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
      }
    `;
  }

  shouldShowError(): boolean {
    if (this.error) return true;
    return !!(this.ngControl?.touched && this.ngControl?.errors);
  }

  getErrorMessage(): string | undefined {
    if (this.error) return this.error;

    if (this.ngControl?.errors) {
      if (this.ngControl.errors['required']) {
        return 'Dit veld is verplicht';
      }
      if (this.ngControl.errors['email']) {
        return 'Vul een geldig e-mailadres in';
      }
      if (this.ngControl.errors['minlength']) {
        const minLength = this.ngControl.errors['minlength'].requiredLength;
        const fieldName = this.label || 'Dit veld';
        return `${fieldName} moet minimaal ${minLength} tekens lang zijn`;
      }
      if (this.ngControl.errors['passwordMismatch']) {
        return 'Wachtwoorden komen niet overeen';
      }
      if (this.ngControl.errors['password']) {
        if (Array.isArray(this.ngControl.errors['password'])) {
          return this.ngControl.errors['password'][0];
        }
        return this.ngControl.errors['password'];
      }
      if (this.ngControl.errors['mixed']) {
        return 'Wachtwoord moet minimaal één kleine letter en één hoofdletter bevatten';
      }
      if (this.ngControl.errors['letters']) {
        return 'Wachtwoord moet minimaal één letter bevatten';
      }
      if (this.ngControl.errors['symbols']) {
        return 'Wachtwoord moet minimaal één speciaal teken bevatten';
      }
      if (this.ngControl.errors['numbers']) {
        return 'Wachtwoord moet minimaal één cijfer bevatten';
      }
      if (this.ngControl.errors['min']) {
        return 'Wachtwoord moet minimaal 8 karakters bevatten';
      }
      if (this.ngControl.errors['confirmed']) {
        return 'Wachtwoord bevestiging komt niet overeen';
      }
      if (this.ngControl.errors['invalid']) {
        return 'De code is niet geldig';
      }
    }
    return undefined;
  }

  writeValue(value: string | boolean): void {
    if (this.type === 'checkbox') {
      this.value = typeof value === 'string' ? value === 'true' : !!value;
    } else {
      this.value = value?.toString() ?? '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value = value;
    this.onChange(value);
  }

  onCheckboxChange(event: Event): void {
    event.preventDefault();
    this.value = !this.value;
    this.onChange(this.value);
  }
}
